import React, { useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const authField = 'w-full bg-nw-black/60 border border-nw-gold/30 rounded-[3px] px-4 h-12 text-nw-ivory placeholder:text-nw-warmgrey/50 focus:outline-none focus:border-nw-gold focus-visible:ring-1 focus-visible:ring-nw-gold/60 text-sm transition-colors';
export const authLabel = 'block text-[10px] tracking-luxe uppercase text-nw-gold/90 mb-2';

const errorMessage = (e, t) => {
  const status = e?.response?.status;
  if (status === 409) return t.account.exists;
  if (status === 401) return t.account.invalid;
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return t.common.error;
};

/** Sign-in form — structure follows the NICEWINE reference: title, subtitle, EMAIL, PASSWORD, gold button, switch link. */
export const SignInForm = ({ onSuccess, onSwitch, idPrefix = 'signin' }) => {
  const { t, loginCustomer } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const c = await loginCustomer(email.trim(), password);
      toast.success(`${t.account.welcomeBack}, ${c.full_name.split(' ')[0]}`);
      onSuccess?.(c);
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className="space-y-6">
      <div>
        <h1 className="font-serif text-4xl text-nw-ivory">{t.account.signIn}</h1>
        <p className="mt-2 text-sm text-nw-warmgrey">{t.account.signInSub}</p>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-email`} className={authLabel}>{t.account.email}</label>
        <input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email-input`} type="email" required autoComplete="email"
          className={authField} value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-password`} className={authLabel}>{t.account.password}</label>
        <input id={`${idPrefix}-password`} data-testid={`${idPrefix}-password-input`} type="password" required autoComplete="current-password"
          className={authField} value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <button type="submit" data-testid={`${idPrefix}-submit-button`} disabled={loading} className="nw-btn-gold-solid w-full h-12">
        {loading ? <Loader2 size={16} className="animate-spin" /> : t.account.signInBtn}
      </button>
      <p className="text-sm text-nw-warmgrey">
        {t.account.newTo}{' '}
        <button type="button" data-testid={`${idPrefix}-switch-to-signup`} onClick={onSwitch} className="text-nw-ivory hover:text-nw-gold underline-offset-4 hover:underline transition-colors">{t.account.createLink}</button>
      </p>
    </form>
  );
};

/** Sign-up form — FULL NAME, EMAIL, PASSWORD, gold button, switch link. */
export const SignUpForm = ({ onSuccess, onSwitch, idPrefix = 'signup', intro }) => {
  const { t, registerCustomer } = useStore();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const c = await registerCustomer({ full_name: fullName.trim(), email: email.trim(), password });
      toast.success(`${t.account.welcome}, ${c.full_name.split(' ')[0]} — ${t.account.created}`);
      onSuccess?.(c);
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className="space-y-6">
      <div>
        <h1 className="font-serif text-4xl text-nw-ivory">{t.account.createTitle}</h1>
        <p className="mt-2 text-sm text-nw-warmgrey">{intro || t.account.createSub}</p>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-name`} className={authLabel}>{t.account.fullName}</label>
        <input id={`${idPrefix}-name`} data-testid={`${idPrefix}-name-input`} required minLength={2} autoComplete="name"
          className={authField} value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-email`} className={authLabel}>{t.account.email}</label>
        <input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email-input`} type="email" required autoComplete="email"
          className={authField} value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-password`} className={authLabel}>{t.account.password}</label>
        <input id={`${idPrefix}-password`} data-testid={`${idPrefix}-password-input`} type="password" required minLength={6} autoComplete="new-password"
          placeholder={t.account.passwordHint} className={authField} value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <button type="submit" data-testid={`${idPrefix}-submit-button`} disabled={loading} className="nw-btn-gold-solid w-full h-12">
        {loading ? <Loader2 size={16} className="animate-spin" /> : t.account.createBtn}
      </button>
      <p className="text-sm text-nw-warmgrey">
        {t.account.already}{' '}
        <button type="button" data-testid={`${idPrefix}-switch-to-signin`} onClick={onSwitch} className="text-nw-ivory hover:text-nw-gold underline-offset-4 hover:underline transition-colors">{t.account.signInLink}</button>
      </p>
    </form>
  );
};
